import React, { useState, useContext, useMemo, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useSearchParams } from "react-router-dom";
import { gql, useQuery, useMutation } from "@apollo/client";
import Spinner from "../../components/Spinner";
import StarekContext from "../../context/StarekContext";
import PartSidebar from "./PartSidebar";
import PartsCardView from "./PartsCardView";
import PartsListingView from "./PartsListingView";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { usePermissions } from "../../hooks/usePermissions";
import "../../starek.css";

const UI_STRINGS = {
    PAGE_TITLE: "Parts",
    SEARCH_PLACEHOLDER: "Search",
    VIEW_MODE_CARD: "Card View",
    VIEW_MODE_LIST: "List View",
    ENTITY_SINGULAR: "Part",
    ENTITY_PLURAL: "Parts",
    DELETE_TITLE_SINGLE: (name) => `Delete Part ${name}?`,
    DELETE_TITLE_BULK: (count) => `Delete ${count} Parts?`,
    DELETE_MESSAGE_SINGLE: (name) => `You are about to delete part "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_MESSAGE_BULK: (count) => `You are about to delete ${count} part(s).

This action cannot be undone.

Are you sure you wish to continue?`,
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    TOTAL_SELECTED: (count) => `Total parts selected: ${count}`,
    ERROR_NO_USER: "No current user in context",
};

const Parts = () => {
    const starekContext = useContext(StarekContext);
    const [searchParams, setSearchParams] = useSearchParams();

    const { can } = usePermissions();
    const canWrite   = can("part", "WRITE");
    const canDestroy = can("part", "DESTROY");

    const GET_PARTS_LIST_FOR_PLANT = gql`
        query GetParts($plantId: Int!) {
            parts(where: { plant_id: { equals: $plantId } }) {
                id
                name
                description
                created_at
                updated_at
                image_asset {
                    url
                }
                created_by {
                    id
                    name
                }
                updated_by {
                    id
                    name
                }
            }
        }
    `;

    const DELETE_ONE_PART_MUTATION = gql`
        mutation DeleteOnePart($where: PartWhereUniqueInput!) {
            deleteOnePart(where: $where) {
                id
                deleted_at
            }
        }
    `;

    const DELETE_MANY_PARTS_MUTATION = gql`
        mutation DeleteManyParts($ids: [Int!]!) {
            deleteManyPart(where: { id: { in: $ids } }) {
                count
            }
        }
    `;

    const CREATE_ONE_PART_MUTATION = gql`
        mutation CreateOnePart($data: PartCreateInput!) {
            createOnePart(data: $data) {
                id
                name
                description
            }
        }
    `;

    const UPDATE_ONE_PART_MUTATION = gql`
        mutation UpdateOnePart(
            $data: PartUpdateInput!
            $where: PartWhereUniqueInput!
        ) {
            updateOnePart(data: $data, where: $where) {
                id
                name
                description
            }
        }
    `;

    const UPLOAD_ASSET_MUTATION = gql`
        mutation UploadAsset($file: Upload!, $semantic: Int!) {
            uploadAsset(file: $file, semantic: $semantic) {
                id
                url
            }
        }
    `;

    const { data, loading, refetch } = useQuery(GET_PARTS_LIST_FOR_PLANT, {
        client: starekContext.engineClient,
        variables: { plantId: starekContext.lastUsedPlant.id },
    });

    const [deleteOnePart] = useMutation(DELETE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });
    const [deleteManyPart] = useMutation(DELETE_MANY_PARTS_MUTATION, {
        client: starekContext.engineClient,
    });
    const [createOnePart] = useMutation(CREATE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });
    const [updateOnePart] = useMutation(UPDATE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });
    const [uploadAsset] = useMutation(UPLOAD_ASSET_MUTATION, {
        client: starekContext.engineClient,
    });

    const [showAddPartDrawer, setShowAddPartDrawer] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [viewMode, setViewMode] = useState("card");
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [toggleCleared, setToggleCleared] = useState(false);

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam && (viewParam === "card" || viewParam === "list")) {
            setViewMode(viewParam);
        }
    }, [searchParams]);

    const items = data?.parts ?? [];

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });

    const handleSavePart = async (partPayload) => {
        try {
            const currentUserId =
                starekContext?.currentUser?.id ||
                starekContext?.user?.id ||
                starekContext?.me?.id;

            if (!currentUserId) {
                throw new Error(UI_STRINGS.ERROR_NO_USER);
            }

            let assetId = null;
            if (partPayload.image_file) {
                const { data: assetData } = await uploadAsset({
                    variables: { file: partPayload.image_file, semantic: 0 },
                });
                assetId = assetData?.uploadAsset?.id;
            }

            const base = {
                name: partPayload.name,
                description: partPayload.description || "",
                plant: { connect: { id: Number(partPayload.plant_id) } },
                ...(assetId && {
                    image_asset: { connect: { id: assetId } },
                }),
            };

            if (!partPayload.id) {
                // Create
                const data = {
                    ...base,
                    created_by: { connect: { id: Number(currentUserId) } },
                    updated_by: { connect: { id: Number(currentUserId) } },
                };
                await createOnePart({ variables: { data } });
            } else {
                // Update - only send fields that have values
                const data = {
                    updated_by: { connect: { id: Number(currentUserId) } },
                };

                if (partPayload.name) {
                    data.name = { set: partPayload.name };
                }

                if (partPayload.description !== undefined) {
                    data.description = { set: partPayload.description || "" };
                }

                if (assetId) {
                    data.image_asset = { connect: { id: assetId } };
                }

                await updateOnePart({
                    variables: { data, where: { id: partPayload.id } },
                });
            }

            await handleDrawerClose(true, false);
            starekContext.showSuccess(
                partPayload.id
                    ? `Part "${partPayload.name}" updated successfully`
                    : `Part "${partPayload.name}" created successfully`,
            );
        } catch (error) {
            starekContext.showError(error);
            throw error;
        }
    };

    const handleAdd = () => {
        setSelectedRows([]);
        setShowAddPartDrawer(true);
    };

    const handleEditSelected = () => {
        if (selectedRows.length !== 1) return;
        setShowAddPartDrawer(true);
    };

    const handleDelete = (parts) => {
        if (!parts || parts.length === 0 || isDeleting) return;

        const isSingle = parts.length === 1;
        const title = isSingle
            ? UI_STRINGS.DELETE_TITLE_SINGLE(parts[0].name)
            : UI_STRINGS.DELETE_TITLE_BULK(parts.length);

        const message = isSingle
            ? UI_STRINGS.DELETE_MESSAGE_SINGLE(parts[0].name)
            : UI_STRINGS.DELETE_MESSAGE_BULK(parts.length);

        openConfirm({
            title: title,
            content: (
                <>
                    <div
                        className="text-dark"
                        style={{ whiteSpace: "pre-wrap" }}
                    >
                        {message}
                    </div>
                    <p className="mt-4 text-dark fw-bold">
                        {UI_STRINGS.TOTAL_SELECTED(parts.length)}
                        &nbsp;
                        <span className="badge bg-info">{parts.length}</span>
                    </p>
                </>
            ),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            type: "danger",
            onConfirm: async () => {
                setIsDeleting(true);
                setDeleteDone(0);

                const ids = parts.map((p) => p.id);

                try {
                    // Try batch delete first
                    await deleteManyPart({ variables: { ids } });
                    setDeleteDone(ids.length);
                    starekContext.showSuccess(
                        `Successfully deleted ${ids.length} part(s)`,
                    );
                } catch {
                    // Fallback to individual deletes
                    for (const id of ids) {
                        try {
                            await deleteOnePart({
                                variables: { where: { id } },
                            });
                            setDeleteDone((d) => d + 1);
                        } catch (err) {
                            starekContext.showError?.(err);
                            break;
                        }
                    }
                } finally {
                    setIsDeleting(false);
                    setSelectedRows([]);
                    setToggleCleared((p) => !p);
                    await refetch();
                    closeConfirm();
                }
            },
            onClose: closeConfirm,
        });
    };

    const handleDrawerClose = async (itemModified, itemDeleted) => {
        setShowAddPartDrawer(false);
        setSelectedRows([]);
        setToggleCleared((p) => !p);

        await new Promise((resolve) => setTimeout(resolve, 0));
        await refetch();
    };

    return loading ? (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ minHeight: "100vh" }}
        >
            <div className="text-xs d-flex justify-content-center">
                <i className="fa fa-cube fa-10x text-success"></i>
            </div>
            <Spinner />
        </div>
    ) : (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{
                    singular: UI_STRINGS.ENTITY_SINGULAR,
                    plural: UI_STRINGS.ENTITY_PLURAL,
                }}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                searchPlaceholder={UI_STRINGS.SEARCH_PLACEHOLDER}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    setSearchParams({ view: mode });
                }}
                onDelete={() => handleDelete(selectedRows)}
                onEdit={handleEditSelected}
            >
                <EntityOperationBar.View
                    mode="card"
                    icon="fa-th"
                    tooltip={UI_STRINGS.VIEW_MODE_CARD}
                >
                    <PartsCardView
                        items={items}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                    />
                </EntityOperationBar.View>

                <EntityOperationBar.View
                    mode="list"
                    icon="fa-list-ul"
                    tooltip={UI_STRINGS.VIEW_MODE_LIST}
                >
                    <PartsListingView
                        items={items}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        filterText={filterText}
                        toggleCleared={toggleCleared}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            {showAddPartDrawer && (
                <PartSidebar
                    show={true}
                    item={selectedRows.length === 1 ? selectedRows[0] : null}
                    plantId={starekContext.lastUsedPlant.id}
                    onDiscard={handleDrawerClose}
                    onSave={handleSavePart}
                    onDelete={(part) => handleDelete([part])}
                />
            )}

            <ConfirmModal
                open={confirmCfg.open}
                title={confirmCfg.title}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                type={confirmCfg.type}
                loading={isDeleting}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            >
                {confirmCfg.content}
            </ConfirmModal>
        </div>
    );
};

export { Parts };
